/**
 * Provenance panel — "What Altara knows about me"
 * Consumer-facing POPIA disclosure. Every stored signal, grouped by
 * category, with a one-tap purge per category.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { WalletShell, useWalletActive, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { loadWallet } from "@/lib/wallet-storage";
import { ShieldCheck, Trash2, Info, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CATEGORY_HINTS = {
  "Engagement": "When you open the wallet, how long you use it, which screens you view.",
  "Product review": "Every step of a banker-pushed product review — status, pre/post confidence, decisions.",
  "Documents": "Which documents you opened and when.",
  "AI conversations": "Questions you asked NAVI (question length + language only, never the wording itself once forgotten).",
  "Glossary lookups": "Terms you searched or opened.",
  "Learning": "Lessons you opened and completed.",
  "Scam Shield checks": "Length + risk band + red-flag labels of texts you analysed. We never store the raw message.",
  "Financial literacy": "Your baseline score and band.",
  "Accessibility preferences": "Font size, contrast, motion, language changes.",
  "Trust ratings": "Ratings you gave banks or products.",
  "Outcome reports": "Positive or negative outcome moments you logged.",
  "Family & sharing": "Trusted contacts added, review shares.",
  "Other": "Miscellaneous events not otherwise categorised.",
};

export default function WalletProvenance() {
  const active = useWalletActive();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [purging, setPurging] = useState(null);

  const load = () => {
    const w = loadWallet() || {};
    if (!w.device_id) { setData({ total_signals: 0, categories: [] }); setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/wallet/provenance`, { params: { device_id: w.device_id, consumer_id: w.consumer_id || undefined } })
      .then(r => setData(r.data))
      .catch(() => setData({ total_signals: 0, categories: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const purge = async (category) => {
    const w = loadWallet() || {};
    if (!window.confirm(`Delete everything Altara has stored under "${category}"? This cannot be undone.`)) return;
    setPurging(category);
    try {
      await axios.delete(`${API}/wallet/provenance`, { params: { device_id: w.device_id, consumer_id: w.consumer_id || undefined, category } });
      load();
    } finally { setPurging(null); }
  };

  return (
    <WalletShell activeTab={active}>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="wallet-provenance">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Your data</div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>What Altara knows about you</h1>
          <p className="text-sm text-slate-600 mt-1">
            Every signal we&rsquo;ve stored for this device, grouped by category. You can delete any category at any time.
          </p>
        </header>

        <section className="rounded-2xl p-4" style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55` }}>
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
            <div className="text-[12px] text-slate-700">
              <strong style={{ color: NAVY }}>Our principle</strong>: signals never include raw personal content. Scam Shield checks store length + risk band, never your messages. NAVI questions store length + language, never the wording. Documents store which files you opened, never their contents.
            </div>
          </div>
        </section>

        {loading ? (
          <div className="flex items-center gap-2 justify-center py-10 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your data…
          </div>
        ) : data.total_signals === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-8 text-center">
            <Info className="w-6 h-6 mx-auto text-slate-400" />
            <div className="mt-2 text-sm text-slate-600">No signals stored yet. As you use the wallet, this page will populate.</div>
          </div>
        ) : (
          <>
            <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: NAVY }}>Total signals stored</div>
                <div className="font-display text-3xl font-semibold" style={{ color: NAVY }}>{data.total_signals}</div>
              </div>
              <div className="text-[11px] text-slate-500">across {data.categories.length} categor{data.categories.length === 1 ? "y" : "ies"}</div>
            </div>

            {data.categories.map(cat => {
              const isOpen = expanded[cat.category];
              return (
                <div key={cat.category} className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden" data-testid={`prov-cat-${cat.category.replace(/\s+/g, "-")}`}>
                  <button
                    onClick={() => setExpanded(e => ({ ...e, [cat.category]: !isOpen }))}
                    className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition"
                  >
                    <div>
                      <div className="text-sm font-semibold" style={{ color: NAVY }}>{cat.category}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{cat.count} signal{cat.count !== 1 ? "s" : ""} · last {cat.last_at ? new Date(cat.last_at).toLocaleString() : "—"}</div>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-4 border-t border-slate-100">
                      <p className="text-[12px] text-slate-600 mt-3">{CATEGORY_HINTS[cat.category] || "Events in this category."}</p>
                      <div className="mt-3 space-y-2">
                        {cat.samples.slice(0, 5).map((s, i) => (
                          <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 p-2">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{s.event_type}</div>
                            {Object.keys(s.payload || {}).length > 0 && (
                              <div className="mt-1 text-[11px] font-mono text-slate-700 break-all">{JSON.stringify(s.payload)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => purge(cat.category)}
                        disabled={purging === cat.category}
                        data-testid={`prov-delete-${cat.category.replace(/\s+/g, "-")}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {purging === cat.category ? "Deleting…" : `Delete all ${cat.category.toLowerCase()} data`}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className="text-[10px] text-slate-400 text-center pt-2">
          Under POPIA you have the right to see, correct and delete your personal information. Emailing <a href="mailto:privacy@altara.example" className="underline">privacy@altara.example</a> also works.
        </div>
      </div>
    </WalletShell>
  );
}
